import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { Card, SectionHeading, Loading, ErrorBox, TH, TD } from '../components/metrics/MetricsUI';
import {
  fetchTrialAccess,
  createTrialApi,
  fetchTrials,
  extendTrialApi,
  endTrialApi,
  resetTrialPasswordApi,
  addTrialAccountsApi,
  removeTrialAccountApi,
  sendTrialEmailApi,
} from '../apis/trial.api';
import {
  REGION_ORDER,
  envLabel,
  endOfDay,
  addDays,
  daysUntil,
  fmtLongDate,
  fmtShortDate,
  fmtTime,
  previewSlug,
  credentialsBlock,
  emailSubject,
  emailBody,
  EMAIL_SIGNATURE,
} from '../components/trial/trialText';

/**
 * Sales trials: a throwaway organisation, N accounts per region with one shared
 * password, access ending on a chosen date.
 *
 * Who sees this is decided by the server (TRIAL_MANAGER_EMAILS), not by role:
 * the page asks /trial/access and shows nothing to anyone else. The server
 * re-checks the allowlist on every call.
 */

const PRESET_DAYS = [7, 14, 21, 30, 45, 60, 90];

const INPUT =
  'block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-lavender focus:outline-none focus:ring-1 focus:ring-lavender sm:text-sm';
const BTN_PRIMARY =
  'inline-flex items-center justify-center rounded-md bg-lavender px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300';
const BTN =
  'inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm hover:shadow disabled:cursor-not-allowed disabled:opacity-50';

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API refused (http, or permissions): the old way.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
};

function CopyButton({ text, label = 'Copy', className = BTN }) {
  const [state, setState] = useState('');
  const onClick = async () => {
    setState((await copyText(text)) ? 'Copied' : 'Copy failed');
    setTimeout(() => setState(''), 1800);
  };
  return (
    <button type="button" onClick={onClick} className={className}>
      {state || label}
    </button>
  );
}

function StatusPill({ status, expiresAt }) {
  const soon = status === 'active' && daysUntil(new Date(expiresAt)) <= 2;
  const tone =
    status === 'active'
      ? soon
        ? 'bg-amber-50 text-amber-700 ring-amber-200'
        : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : 'bg-gray-100 text-gray-600 ring-gray-200';
  const label = status === 'active' ? (soon ? 'Ending soon' : 'Active') : status === 'expired' ? 'Expired' : 'Ended early';
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${tone}`}>{label}</span>;
}

/**
 * Preset buttons plus a calendar showing the whole access window: every day from
 * `start` (today for a new trial, the trial's own start when changing one)
 * through the end is highlighted. `value` is the chosen end, a Date at 23:59:59
 * local; until one is chosen, `current` (an existing trial's end) is shown.
 */
function EndDatePicker({ value, onChange, maxDays = 365, start, current }) {
  const today = new Date();
  const from = start ? new Date(start) : today;
  const shownEnd = value || (current ? new Date(current) : null);
  const daysLeft = value ? daysUntil(value) : null;
  const totalDays = shownEnd ? daysUntil(shownEnd, from) + 1 : null;
  return (
    <div className="flex flex-col gap-4 md:flex-row">
      <div className="trial-datepicker">
        <DatePicker
          inline
          selectsEnd
          selected={shownEnd}
          startDate={from}
          endDate={shownEnd}
          openToDate={shownEnd || today}
          onChange={(d) => d && onChange(endOfDay(d))}
          minDate={addDays(today, 1)}
          maxDate={addDays(today, maxDays)}
        />
      </div>
      <div className="flex-1">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Quick pick — days from today</p>
        <div className="flex flex-wrap gap-2">
          {PRESET_DAYS.filter((d) => d <= maxDays).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onChange(endOfDay(addDays(today, d)))}
              className={`rounded-full px-3 py-1 text-sm ring-1 ${
                daysLeft === d
                  ? 'bg-lavender text-white ring-lavender'
                  : 'bg-white text-gray-700 ring-gray-200 hover:ring-lavender'
              }`}
            >
              {d} days
            </button>
          ))}
        </div>
        {shownEnd && (
          <div className="mt-4 space-y-0.5 text-sm text-gray-700">
            <p>
              Starts <strong>{fmtLongDate(from)}</strong>
            </p>
            <p>
              Ends <strong>{fmtLongDate(shownEnd)}</strong> at {fmtTime(shownEnd)}
              {!value && <span className="text-gray-500"> (current)</span>}
            </p>
            <p className="text-gray-500">
              {totalDays} day{totalDays === 1 ? '' : 's'} in total
              {value ? ` · ${daysLeft} from today` : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/** What was just created (or re-passworded): credentials, the email, and sending it. */
function TrialResult({ result, onClose, idToken, emailCc = [] }) {
  const { trial, password, failures = [], warnings = [] } = result;
  const [subject, setSubject] = useState(() => emailSubject(trial));
  const [body, setBody] = useState(() => emailBody(trial, password));
  const [sendTo, setSendTo] = useState('');
  const [sending, setSending] = useState(false);
  const [sendState, setSendState] = useState(null); // { tone, text }

  // Bring the result into view the moment it exists. scrollIntoView rather than
  // window.scrollTo: the page scrolls inside the app's layout, not the window.
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const send = async () => {
    setSending(true);
    setSendState(null);
    try {
      const d = await sendTrialEmailApi(idToken, { to: sendTo, subject, body });
      setSendState({
        tone: 'ok',
        text: `Sent to ${d.to.join(', ')}${d.cc.length ? ` (cc ${d.cc.join(', ')})` : ''} at ${fmtTime(new Date())}.`,
      });
      setSendTo('');
    } catch (e) {
      setSendState({ tone: 'error', text: e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div ref={ref} className="scroll-mt-4">
    <Card className="mb-8 border-2 border-emerald-200">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-navy">
            {trial.companyName}: {trial.accountCount} account{trial.accountCount === 1 ? '' : 's'} ready
          </h2>
          <p className="text-sm text-gray-500">
            Organisation <span className="font-mono">{trial.orgName}</span> · access ends {fmtLongDate(trial.expiresAt)} at{' '}
            {fmtTime(trial.expiresAt)}
          </p>
        </div>
        <button type="button" onClick={onClose} className={BTN}>
          Done
        </button>
      </div>

      {failures.length > 0 && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          <p className="font-semibold">{failures.length} account(s) could not be created:</p>
          <ul className="ml-5 list-disc">
            {failures.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <details className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <summary className="cursor-pointer font-semibold">
            {warnings.length} registry/subscription warning(s) — the accounts still work
          </summary>
          <ul className="ml-5 mt-2 list-disc">
            {warnings.map((w) => (
              <li key={w} className="break-all">
                {w}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-md bg-gray-50 p-4">
        <span className="text-sm text-gray-600">Password for every account</span>
        <code className="rounded bg-white px-3 py-1 font-mono text-lg tracking-wider text-navy ring-1 ring-gray-200">
          {password}
        </code>
        <CopyButton text={password} label="Copy password" />
        <span className="w-full text-xs text-gray-500">
          The password is not stored anywhere. Copy it now — if it is lost, use “Reset password” on the trial below.
        </span>
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-navy">Accounts</p>
        <CopyButton text={credentialsBlock(trial, password)} label="Copy accounts + password" />
      </div>
      <div className="mb-6 grid gap-4 md:grid-cols-3">
        {trial.parts.map((part) => (
          <div key={part.target} className="rounded-md border border-gray-200 p-3">
            <div className="mb-1 flex items-center justify-between">
              <p className="text-sm font-semibold text-navy">
                {envLabel(part.env)} · {part.region}{' '}
                <span className="font-normal text-gray-500">({part.emails.length})</span>
              </p>
              <CopyButton text={part.emails.join('\n')} label="Copy" className={`${BTN} !px-2 !py-0.5 text-xs`} />
            </div>
            <p className="mb-2 break-all text-xs text-gray-400">Sign in at {part.loginUrl || '—'}</p>
            <ul className="space-y-1">
              {part.emails.map((e) => (
                <li key={e} className="break-all font-mono text-xs text-gray-700">
                  {e}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mb-3">
        <SectionHeading eyebrow="Ready to send" title="Email draft" />
      </div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Subject</label>
      <div className="mb-3 flex gap-2">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} className={INPUT} />
        <CopyButton text={subject} label="Copy subject" className={`${BTN} w-36 shrink-0`} />
      </div>
      <div className="mb-1 flex items-end justify-between gap-2">
        <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">
          Body — edit freely before copying or sending
        </label>
        <CopyButton text={body} label="Copy email" className={`${BTN_PRIMARY} w-36 shrink-0`} />
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={Math.min(40, body.split('\n').length + 2)}
        className={`${INPUT} font-mono text-xs leading-5`}
      />

      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
        <label htmlFor="trial-send-to" className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
          Send to
        </label>
        <div className="flex gap-2">
          <input
            id="trial-send-to"
            type="text"
            value={sendTo}
            onChange={(e) => setSendTo(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendTo.trim() && !sending && send()}
            placeholder="name@company.com — separate several with commas"
            autoComplete="off"
            className={INPUT}
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !sendTo.trim()}
            className={`${BTN_PRIMARY} w-36 shrink-0`}
          >
            {sending ? 'Sending…' : 'Send email'}
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          From {EMAIL_SIGNATURE} · CC: {emailCc.length ? emailCc.join(', ') : 'none set yet'}
        </p>
        {sendState && (
          <p className={`mt-1 text-sm ${sendState.tone === 'error' ? 'text-rose-700' : 'text-emerald-700'}`}>
            {sendState.text}
          </p>
        )}
      </div>
    </Card>
    </div>
  );
}

/**
 * One box of a trial (e.g. Staging · US): its accounts, each removable, and a
 * "+ Add" that creates more on the same server.
 */
function TrialPart({ trial, part, idToken, onRefresh, maxPerTarget }) {
  const [adding, setAdding] = useState(false);
  const [count, setCount] = useState('1');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [added, setAdded] = useState(null); // { created, password, sharedPassword }
  const active = part.status === 'active';

  const add = async () => {
    setBusy('add');
    setMessage(null);
    try {
      const d = await addTrialAccountsApi(idToken, trial.id, part.target, parseInt(count, 10) || 1, password.trim() || undefined);
      setAdded(d);
      setAdding(false);
      setPassword('');
      setCount('1');
      if (d.failures.length) setMessage({ tone: 'error', text: `${d.failures.length} could not be added: ${d.failures[0]}` });
      else if (d.warnings.length) setMessage({ tone: 'warn', text: `${d.warnings.length} subscription warning(s): ${d.warnings[0]}` });
      onRefresh();
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
    } finally {
      setBusy('');
    }
  };

  const remove = async (email) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${email}?\n\nThe account and its login are removed permanently. This cannot be undone.`)) return;
    setBusy(email);
    setMessage(null);
    try {
      const d = await removeTrialAccountApi(idToken, trial.id, part.target, email);
      if (d.warnings.length) setMessage({ tone: 'warn', text: `Deleted, with ${d.warnings.length} warning(s): ${d.warnings[0]}` });
      onRefresh();
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="rounded border border-gray-200 bg-white p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-gray-600">
          {envLabel(part.env)} · {part.region} <span className="font-normal text-gray-400">({part.emails.length})</span>
          <span className="block font-normal text-gray-400">{part.loginUrl}</span>
        </p>
        <button
          type="button"
          onClick={() => setAdding(!adding)}
          disabled={!active || !!busy}
          title={active ? 'Add accounts here' : 'Reopen the trial to add accounts'}
          className={`${BTN} !px-2 !py-0.5 text-xs`}
        >
          {adding ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {adding && (
        <div className="my-2 space-y-2 rounded bg-gray-50 p-2">
          <div className="flex items-center gap-2">
            <label htmlFor={`add-${part.target}`} className="text-xs text-gray-600">How many</label>
            <input
              id={`add-${part.target}`}
              type="number"
              min={1}
              max={maxPerTarget}
              value={count}
              onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, ''))}
              className={`${INPUT} !w-20 !py-1`}
            />
          </div>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Trial's current password (blank = new password for these)"
            autoComplete="off"
            className={`${INPUT} !py-1 font-mono text-xs`}
          />
          <p className="text-xs text-gray-500">
            Type the password you already sent so everyone keeps one password. The system does not store it, so it cannot
            fill it in for you.
          </p>
          <button type="button" onClick={add} disabled={!!busy} className={`${BTN_PRIMARY} !px-3 !py-1 text-xs`}>
            {busy === 'add' ? 'Adding…' : `Add to ${envLabel(part.env)} ${part.region}`}
          </button>
        </div>
      )}

      {added && (
        <div className="my-2 rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="font-semibold">Added {added.created.length}</span>
            <div className="flex gap-1">
              <CopyButton
                text={`${added.created.join('\n')}${added.password ? `\n\nPassword: ${added.password}` : ''}`}
                label="Copy"
                className={`${BTN} !px-2 !py-0.5 text-xs`}
              />
              <button type="button" onClick={() => setAdded(null)} className={`${BTN} !px-2 !py-0.5 text-xs`}>
                Close
              </button>
            </div>
          </div>
          {added.created.map((e) => (
            <p key={e} className="break-all font-mono">{e}</p>
          ))}
          <p className="mt-1">
            {added.password ? (
              <>
                Password for these accounts only: <code className="font-mono font-semibold">{added.password}</code> — not
                stored, copy it now.
              </>
            ) : (
              'They use the password you typed.'
            )}
          </p>
        </div>
      )}

      {message && (
        <p className={`my-1 text-xs ${message.tone === 'error' ? 'text-rose-700' : 'text-amber-700'}`}>{message.text}</p>
      )}

      <ul className="space-y-0.5">
        {part.emails.map((e) => (
          <li key={e} className="group flex items-center justify-between gap-2 rounded px-1 hover:bg-gray-50">
            <span className="break-all font-mono text-xs text-gray-700">{e}</span>
            <button
              type="button"
              onClick={() => remove(e)}
              disabled={!!busy}
              aria-label={`Delete ${e}`}
              title="Delete this account"
              className="shrink-0 rounded px-1.5 text-sm leading-5 text-gray-400 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40"
            >
              {busy === e ? '…' : '×'}
            </button>
          </li>
        ))}
        {part.emails.length === 0 && <li className="text-xs text-gray-400">No accounts left here.</li>}
      </ul>
    </div>
  );
}

function TrialRow({ trial, idToken, onChanged, onPassword, onRefresh, maxDays, maxPerTarget }) {
  const [open, setOpen] = useState(false);
  const [newEnd, setNewEnd] = useState(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);

  const act = async (label, fn) => {
    setBusy(label);
    setMessage(null);
    try {
      const d = await fn();
      if (d.warnings && d.warnings.length) setMessage({ tone: 'warn', text: `${d.warnings.length} warning(s): ${d.warnings[0]}` });
      onChanged(d.trial);
      return d;
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
      return null;
    } finally {
      setBusy('');
    }
  };

  const saveEnd = async () => {
    const d = await act('extend', () => extendTrialApi(idToken, trial.id, newEnd.toISOString()));
    if (d) setNewEnd(null);
  };
  const endNow = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`End the ${trial.companyName} trial now? All ${trial.accountCount} accounts stop working immediately.`)) {
      act('end', () => endTrialApi(idToken, trial.id));
    }
  };
  const resetPassword = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Give all ${trial.accountCount} ${trial.companyName} accounts a new password? The old one stops working.`)) return;
    const d = await act('password', () => resetTrialPasswordApi(idToken, trial.id));
    if (d) onPassword(d);
  };

  return (
    <>
      <tr className="border-t border-gray-100">
        <td className={TD}>
          <button type="button" onClick={() => setOpen(!open)} className="text-left">
            <span className="font-semibold text-navy">{trial.companyName}</span>
            <span className="block font-mono text-xs text-gray-400">{trial.orgName}</span>
          </button>
        </td>
        <td className={TD}>
          {trial.parts.map((p) => `${p.emails.length} ${envLabel(p.env)} ${p.region}`).join(' · ')}
        </td>
        <td className={TD}>
          {fmtShortDate(trial.expiresAt)}
          {trial.status === 'active' && (
            <span className="block text-xs text-gray-400">
              {Math.max(0, daysUntil(new Date(trial.expiresAt)))} day(s) left
            </span>
          )}
        </td>
        <td className={TD}>
          <StatusPill status={trial.status} expiresAt={trial.expiresAt} />
        </td>
        <td className={`${TD} text-xs`}>{trial.createdBy}</td>
        <td className={TD}>
          <button type="button" onClick={() => setOpen(!open)} className={BTN}>
            {open ? 'Hide' : 'Manage'}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-4 py-4">
            {message && (
              <p className={`mb-3 text-sm ${message.tone === 'error' ? 'text-rose-700' : 'text-amber-700'}`}>{message.text}</p>
            )}
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-semibold text-navy">
                  {trial.status === 'active' ? 'Change end date' : 'Reopen with a new end date'}
                </p>
                <EndDatePicker
                  value={newEnd}
                  onChange={setNewEnd}
                  maxDays={maxDays}
                  start={trial.startsAt}
                  current={trial.expiresAt}
                />
                <button type="button" disabled={!newEnd || !!busy} onClick={saveEnd} className={`${BTN_PRIMARY} mt-3`}>
                  {busy === 'extend' ? 'Saving…' : trial.status === 'active' ? 'Save new end date' : 'Reopen trial'}
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <CopyButton text={credentialsBlock(trial, null)} label="Copy accounts" />
                  <CopyButton text={emailBody(trial, null)} label="Copy email (no password)" />
                  <button type="button" onClick={resetPassword} disabled={!!busy} className={BTN}>
                    {busy === 'password' ? 'Resetting…' : 'Reset password'}
                  </button>
                  {trial.status === 'active' && (
                    <button type="button" onClick={endNow} disabled={!!busy} className={`${BTN} text-rose-700`}>
                      {busy === 'end' ? 'Ending…' : 'End trial now'}
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {trial.parts.map((p) => (
                    <TrialPart
                      key={p.target}
                      trial={trial}
                      part={p}
                      idToken={idToken}
                      onRefresh={onRefresh}
                      maxPerTarget={maxPerTarget}
                    />
                  ))}
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">History</p>
                  <ul className="space-y-0.5 text-xs text-gray-600">
                    {[...trial.history].reverse().map((h, i) => (
                      <li key={`${h.at}-${h.action}-${i}`}>
                        {fmtShortDate(h.at)} {fmtTime(h.at)} · {envLabel(h.env)} · <strong>{h.action}</strong> by {h.by}
                        {h.detail ? ` — ${h.detail}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function TrialAccounts() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user, shallowEqual);
  const idToken = user && (user.idToken || user.accessToken);

  const [access, setAccess] = useState(null);
  const [error, setError] = useState(null);
  const [trials, setTrials] = useState(null);
  const [listWarnings, setListWarnings] = useState([]);

  const [companyName, setCompanyName] = useState('');
  const [counts, setCounts] = useState({}); // { 'dev:US': '2', 'staging:EU': '1' }
  const [endDate, setEndDate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState(null);
  const [result, setResult] = useState(null);

  // The removed "Test login" feature kept trial passwords in localStorage. Clear
  // them out of any browser that used it. Safe to delete after a few releases.
  useEffect(() => {
    try {
      localStorage.removeItem('lavenirai.trialPasswords');
    } catch {
      // storage blocked: nothing was saved either
    }
  }, []);

  const loadTrials = useCallback(() => {
    fetchTrials(idToken)
      .then((d) => {
        setTrials(d.trials);
        setListWarnings(d.warnings || []);
      })
      .catch((e) => setError(e.message));
  }, [idToken]);

  useEffect(() => {
    if (!idToken) return undefined;
    let cancelled = false;
    fetchTrialAccess(idToken, true)
      .then((d) => {
        if (cancelled) return;
        setAccess(d);
        if (d.canManage) loadTrials();
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [idToken, loadTrials]);

  // Targets as rows of environments, each with its US / EU / APAC cells.
  const envRows = useMemo(() => {
    const rows = new Map();
    for (const t of (access && access.targets) || []) {
      if (!rows.has(t.env)) rows.set(t.env, {});
      rows.get(t.env)[t.region] = t;
    }
    const order = ['dev', 'staging', 'prod'];
    return [...rows.entries()].sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
  }, [access]);
  // Only the region columns some environment actually has: dev and staging are
  // one US site each, so on dev the grid is a single column.
  const regionCols = REGION_ORDER.filter((r) => ((access && access.targets) || []).some((t) => t.region === r));
  const unavailable = ((access && access.targets) || []).filter((t) => !t.available);
  const total = Object.values(counts).reduce((s, v) => s + (parseInt(v, 10) || 0), 0);
  const slug = previewSlug(companyName);
  const limits = (access && access.limits) || { perTarget: 50, total: 100, maxDays: 365 };

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!companyName.trim()) return setFormError('Enter the company name.');
    if (!slug) return setFormError('The company name needs at least one Latin letter or number to build account names from.');
    if (total < 1) return setFormError('Add at least one user.');
    if (!endDate) return setFormError('Pick when the trial ends.');
    setCreating(true);
    try {
      const payload = {};
      Object.entries(counts).forEach(([key, v]) => {
        const n = parseInt(v, 10) || 0;
        if (n) payload[key] = n;
      });
      const d = await createTrialApi(idToken, { companyName, counts: payload, expiresAt: endDate.toISOString() });
      setResult(d); // TrialResult scrolls itself into view
      setCompanyName('');
      setCounts({});
      setEndDate(null);
      loadTrials();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setCreating(false);
    }
    return undefined;
  };

  const header = (
    <div className="mb-6 flex items-center justify-between">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Trial accounts</h1>
        <p className="text-sm text-gray-500">
          Time-limited demo organisations
          {access && access.environment ? (
            <span className="ml-2 rounded bg-navy px-1.5 py-0.5 text-xs font-semibold uppercase text-white">
              {access.environment}
            </span>
          ) : null}
        </p>
      </div>
      <button onClick={() => navigate('/')} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:shadow">
        ← Home
      </button>
    </div>
  );

  if (!idToken) {
    return (
      <div className="min-h-screen bg-gray-100 p-6 font-metrics">
        <ErrorBox message="Not signed in." onRetry={() => navigate('/login')} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6 font-metrics">
      {/* react-datepicker's stock look, brought in line with the page */}
      <style>{`
        .trial-datepicker .react-datepicker { font-family: inherit; border-color: #e5e7eb; border-radius: 0.5rem; }
        .trial-datepicker .react-datepicker__header { background: #f9fafb; border-bottom-color: #e5e7eb; }
        .trial-datepicker .react-datepicker__day--selected,
        .trial-datepicker .react-datepicker__day--keyboard-selected { background: #6c63ff; color: #fff; border-radius: 9999px; }
        .trial-datepicker .react-datepicker__day:hover { border-radius: 9999px; }
        /* the whole access window, start through end */
        .trial-datepicker .react-datepicker__day--in-range,
        .trial-datepicker .react-datepicker__day--in-selecting-range {
          background: #ebe9ff !important; color: #071146 !important; border-radius: 0 !important;
        }
        .trial-datepicker .react-datepicker__day--range-start {
          background: #6c63ff !important; color: #fff !important; border-radius: 9999px 0 0 9999px !important;
        }
        .trial-datepicker .react-datepicker__day--range-end,
        .trial-datepicker .react-datepicker__day--selected {
          background: #6c63ff !important; color: #fff !important; border-radius: 0 9999px 9999px 0 !important;
        }
        .trial-datepicker .react-datepicker__day--range-start.react-datepicker__day--range-end {
          border-radius: 9999px !important;
        }
      `}</style>
      <div className="mx-auto max-w-[1100px]">
        {header}

        {error && <ErrorBox message={error} />}
        {!access && !error && <Loading what="trial settings" />}
        {access && !access.canManage && (
          <Card>
            <p className="text-gray-700">Trial management is not enabled for your account on this site.</p>
          </Card>
        )}

        {access && access.canManage && (
          <>
            {result && (
              <TrialResult
                key={result.trial.id + result.password}
                result={result}
                onClose={() => setResult(null)}
                idToken={idToken}
                emailCc={access.emailCc || []}
              />
            )}

            <SectionHeading eyebrow="Create" title="New trial" />
            <Card className="mb-8">
              <form onSubmit={submit} className="space-y-6">
                <div>
                  <label htmlFor="trial-company" className="mb-1 block text-sm font-semibold text-navy">
                    Company name
                  </label>
                  <input
                    id="trial-company"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    maxLength={80}
                    placeholder="e.g. Acme Corporation"
                    className={INPUT}
                    autoComplete="off"
                  />
                  <p className="mt-1 break-all text-xs text-gray-500">
                    {slug ? (
                      <>
                        Accounts will look like{' '}
                        <span className="font-mono text-gray-700">
                          {slug}-us-trial-xxxxxxxxxxxx@{access.emailDomain}
                        </span>{' '}
                        in organisation <span className="font-mono text-gray-700">{slug}-trial-xxxxxxxxxxxx</span>. Accents,
                        symbols and capitals are simplified automatically; the email uses the name exactly as typed.
                      </>
                    ) : (
                      'Account names are built from the letters and numbers in the company name.'
                    )}
                  </p>
                </div>

                <div>
                  <p className="mb-1 text-sm font-semibold text-navy">Users per environment and region</p>
                  <p className="mb-2 text-xs text-gray-500">
                    Accounts are created on the server you pick here — not on the one you are signed in to
                    {access.environment ? ` (${envLabel(access.environment)})` : ''}.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr>
                          <th className={`${TH} !px-2`} aria-label="Environment" />
                          {regionCols.map((r) => (
                            <th key={r} className={`${TH} !px-2`}>{r}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {envRows.map(([env, cells]) => (
                          <tr key={env}>
                            <td className="whitespace-nowrap px-2 py-2 text-sm font-semibold text-navy">{envLabel(env)}</td>
                            {regionCols.map((r) => {
                              const t = cells[r];
                              // A region this environment does not have: no box at all.
                              if (!t) return <td key={r} className="px-2 py-2" />;
                              const on = t.available;
                              return (
                                <td key={r} className="px-2 py-2 align-top">
                                  <input
                                    type="number"
                                    min={0}
                                    max={limits.perTarget}
                                    step={1}
                                    inputMode="numeric"
                                    aria-label={`${envLabel(env)} ${r} users`}
                                    value={on ? counts[t.key] || '' : ''}
                                    disabled={!on}
                                    placeholder={on ? '0' : '—'}
                                    title={!t.available ? t.reason : undefined}
                                    onChange={(e) => setCounts({ ...counts, [t.key]: e.target.value.replace(/[^0-9]/g, '') })}
                                    className={`${INPUT} ${on ? '' : 'border-dashed bg-gray-50'}`}
                                  />
                                  {!t.available && <span className="mt-1 block text-xs text-amber-600">Unavailable</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {unavailable.length > 0 && (
                    <details className="mt-2 text-xs text-amber-700">
                      <summary className="cursor-pointer">Why some are unavailable ({unavailable.length})</summary>
                      <ul className="ml-5 mt-1 list-disc">
                        {unavailable.map((t) => (
                          <li key={t.key} className="break-all">
                            <strong>{t.key}</strong>: {t.reason}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Up to {limits.perTarget} per box, {limits.total} per trial. Every account shares one password.
                  </p>
                </div>

                <div>
                  <p className="mb-1 text-sm font-semibold text-navy">Trial ends</p>
                  <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <strong>Add a buffer:</strong> set the end date a few days to a week <em>past</em> the trial length you agreed
                    verbally (agreed 14 days → pick 21). Customers start late, and this way nobody is cut off mid-demo. Access
                    ends automatically at the end of the day you pick.
                  </div>
                  <EndDatePicker value={endDate} onChange={setEndDate} maxDays={limits.maxDays} />
                </div>

                {formError && <p className="text-sm font-medium text-rose-700">{formError}</p>}

                <div className="flex items-center gap-3">
                  <button type="submit" disabled={creating} className={BTN_PRIMARY}>
                    {creating ? `Creating ${total} account${total === 1 ? '' : 's'}…` : 'Create trial org and users'}
                  </button>
                  {total > 0 && !creating && (
                    <span className="text-sm text-gray-500">
                      {total} account{total === 1 ? '' : 's'}
                      {endDate ? `, ending ${fmtShortDate(endDate)}` : ''}
                    </span>
                  )}
                  {creating && <span className="text-sm text-gray-500">About a second per account — keep this tab open.</span>}
                </div>
              </form>
            </Card>

            <SectionHeading eyebrow="Track" title="All trials" subtitle="Accounts are disabled automatically when a trial ends." />
            {listWarnings.length > 0 && (
              <p className="mb-2 text-xs text-amber-700">
                Some servers did not answer, so their trials are missing below: {listWarnings.join('; ')}
              </p>
            )}
            <Card className="!p-0 overflow-x-auto">
              {!trials && <div className="p-6"><Loading what="trials" /></div>}
              {trials && trials.length === 0 && <p className="p-6 text-sm text-gray-500">No trials yet.</p>}
              {trials && trials.length > 0 && (
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={TH}>Company</th>
                      <th className={TH}>Accounts</th>
                      <th className={TH}>Ends</th>
                      <th className={TH}>Status</th>
                      <th className={TH}>Created by</th>
                      <th className={TH} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {trials.map((t) => (
                      <TrialRow
                        key={t.id}
                        trial={t}
                        idToken={idToken}
                        maxDays={limits.maxDays}
                        maxPerTarget={limits.perTarget}
                        onRefresh={loadTrials}
                        onChanged={(nt) => setTrials((list) => list.map((x) => (x.id === nt.id ? nt : x)))}
                        onPassword={setResult}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
